import { useEffect, useMemo } from 'react';
import {
  createRouter,
  RouterProvider,
  type AnyRoute,
} from '@tanstack/react-router';

import { rootRoute, InitialContext, type AppRouterProps } from './routerBase';
import type { Section } from './routerBase';
import type { CurrentUser } from '../lib/capabilities';
import { contactsIndexRoute, contactDetailsRoute, contactEditRoute } from './sections/contacts.routes';
import { inventoriesIndexRoute, inventoryDetailsRoute, inventoryEditRoute } from './sections/inventories.routes';
import { closedHoursIndexRoute, closedHoursEditRoute } from './sections/closedHours.routes';
import { resaleItemsIndexRoute, resaleItemDetailsRoute, resaleItemEditRoute } from './sections/resaleItems.routes';
import { orderSourcesIndexRoute, orderSourceDetailsRoute, orderSourceEditRoute } from './sections/orderSources.routes';
import { priceModifiersIndexRoute, discountEditRoute, surchargeEditRoute } from './sections/priceModifiers.routes';
import { productsIndexRoute, productDetailsRoute, productEditRoute } from './sections/products.routes';
import { eventsIndexRoute } from './sections/events.routes';
import { ordersIndexRoute, orderDetailsRoute, orderEditRoute, orderHistoryRoute, eventHistoryRoute } from './sections/orders.routes';
import { capacitiesIndexRoute, capacitiesBulkEditRoute } from './sections/capacities.routes';
import { presetMenusIndexRoute, presetMenuDetailsRoute, presetMenuEditRoute } from './sections/presetMenus.routes';
import { bundlesIndexRoute, bundleDetailsRoute, bundleEditRoute } from './sections/bundles.routes';
import { locationsIndexRoute, locationEditRoute } from './sections/locations.routes';
import { organisationsIndexRoute, organisationDetailsRoute, organisationEditRoute } from './sections/organisations.routes';
import { usersIndexRoute, userDetailsRoute, userEditRoute } from './sections/users.routes';
import { schedulerIndexRoute } from './sections/scheduler.routes';
import { oauthAppsIndexRoute, oauthAppDetailsRoute, oauthAppEditRoute, oauthScopeRequestsIndexRoute } from './sections/oauth.routes';
import { dashboardIndexRoute, dashboardRootRoute } from './sections/dashboard.routes';
import { salesTrackingIndexRoute } from './sections/salesTracking.routes';

export type { Section } from './routerBase';

const SECTION_ROUTES = {
  contacts: [contactsIndexRoute, contactDetailsRoute, contactEditRoute],
  inventories: [inventoriesIndexRoute, inventoryDetailsRoute, inventoryEditRoute],
  closed_hours: [closedHoursIndexRoute, closedHoursEditRoute],
  resale_items: [resaleItemsIndexRoute, resaleItemDetailsRoute, resaleItemEditRoute],
  order_sources: [orderSourcesIndexRoute, orderSourceDetailsRoute, orderSourceEditRoute],
  price_modifiers: [priceModifiersIndexRoute, discountEditRoute, surchargeEditRoute],
  products: [productsIndexRoute, productDetailsRoute, productEditRoute],
  events: [eventsIndexRoute],
  orders: [ordersIndexRoute, orderDetailsRoute, orderEditRoute, orderHistoryRoute, eventHistoryRoute],
  capacities: [capacitiesIndexRoute, capacitiesBulkEditRoute],
  preset_menus: [presetMenusIndexRoute, presetMenuDetailsRoute, presetMenuEditRoute],
  bundles: [bundlesIndexRoute, bundleDetailsRoute, bundleEditRoute],
  locations: [locationsIndexRoute, locationEditRoute],
  organisations: [organisationsIndexRoute, organisationDetailsRoute, organisationEditRoute],
  users: [usersIndexRoute, userDetailsRoute, userEditRoute],
  scheduler: [schedulerIndexRoute],
  oauth: [oauthAppsIndexRoute, oauthAppDetailsRoute, oauthAppEditRoute, oauthScopeRequestsIndexRoute],
  dashboard: [dashboardIndexRoute, dashboardRootRoute],
  sales_tracking: [salesTrackingIndexRoute],
} satisfies Record<Section, AnyRoute[]>;

// URL prefixes owned by each section's router. Used by CrossSectionFallback
// to distinguish a cross-section popstate (route lives in a sibling section)
// from a genuine intra-section 404 (mistyped deep link, removed route). The
// price_modifiers section owns three top-level paths because discount and
// surcharge edit pages share its sidebar nav group.
export const SECTION_PREFIXES = {
  contacts: ['/contacts'],
  inventories: ['/inventories'],
  closed_hours: ['/closed_hours'],
  resale_items: ['/resale_items'],
  order_sources: ['/order_sources'],
  price_modifiers: ['/price_modifiers', '/discounts', '/surcharges'],
  products: ['/products'],
  events: ['/events'],
  orders: ['/orders'],
  capacities: ['/capacities'],
  preset_menus: ['/preset_menus'],
  bundles: ['/bundles'],
  locations: ['/locations'],
  organisations: ['/organisations'],
  users: ['/users'],
  scheduler: ['/scheduler'],
  oauth: ['/account/oauth_applications', '/account/oauth_scope_requests'],
  // The section owns the bare root path too (`root "dashboard#index"`): '/' as
  // a prefix only exact-matches, since `pathname.startsWith('/' + '/')` never
  // holds for real paths.
  dashboard: ['/dashboard', '/'],
  sales_tracking: ['/sales_tracking'],
} as const satisfies Record<Section, readonly string[]>;

export function pathBelongsToAnySection(pathname: string): boolean {
  return Object.values(SECTION_PREFIXES).some((prefixes) =>
    prefixes.some((p) => pathname === p || pathname.startsWith(p + '/')),
  );
}

// TanStack intercepts popstate on URLs not in its tree and renders this
// boundary instead of letting the browser fall through. If the URL belongs
// to a sibling section, hand control to Turbo via a restore visit so the
// destination remounts AppRouter with its own translations bag. If the URL
// doesn't belong to any known section (intra-section typo, removed route),
// fall through to a regular Turbo visit so the server can render a real 404
// instead of silently looping back here.
function CrossSectionFallback() {
  useEffect(() => {
    const turbo = (window as unknown as {
      Turbo?: { visit: (url: string, opts?: { action?: string }) => void };
    }).Turbo;
    if (!turbo) {
      window.location.reload();
      return;
    }
    if (pathBelongsToAnySection(window.location.pathname)) {
      turbo.visit(window.location.href, { action: 'restore' });
    } else {
      turbo.visit(window.location.href);
    }
  }, []);
  return null;
}

// All routes listed once so the Register augmentation below sees every URL
// in the app. Without this, typed <Link to="..."> and useNavigate APIs lose
// inference outside AppRouter.tsx — runtime routers built by buildAppRouter
// are per-section (only their own routes), but typed navigation needs to
// know about every valid URL anywhere in the app.
const ALL_ROUTES = [
  contactsIndexRoute, contactDetailsRoute, contactEditRoute,
  inventoriesIndexRoute, inventoryDetailsRoute, inventoryEditRoute,
  closedHoursIndexRoute, closedHoursEditRoute,
  resaleItemsIndexRoute, resaleItemDetailsRoute, resaleItemEditRoute,
  orderSourcesIndexRoute, orderSourceDetailsRoute, orderSourceEditRoute,
  priceModifiersIndexRoute, discountEditRoute, surchargeEditRoute,
  productsIndexRoute, productDetailsRoute, productEditRoute,
  eventsIndexRoute,
  ordersIndexRoute, orderDetailsRoute, orderEditRoute, orderHistoryRoute, eventHistoryRoute,
  capacitiesIndexRoute, capacitiesBulkEditRoute,
  presetMenusIndexRoute, presetMenuDetailsRoute, presetMenuEditRoute,
  bundlesIndexRoute, bundleDetailsRoute, bundleEditRoute,
  locationsIndexRoute, locationEditRoute,
  organisationsIndexRoute, organisationDetailsRoute, organisationEditRoute,
  usersIndexRoute, userDetailsRoute, userEditRoute,
  schedulerIndexRoute,
  oauthAppsIndexRoute, oauthAppDetailsRoute, oauthAppEditRoute, oauthScopeRequestsIndexRoute,
  dashboardIndexRoute, dashboardRootRoute,
  salesTrackingIndexRoute,
];

// Typing-only router. Constructed at module load so TanStack's Register
// augmentation has a router whose route tree carries every concrete route
// type. Never actually rendered — runtime mounts use buildAppRouter().
// NB: this must be constructed before any buildAppRouter() call — both mutate
// rootRoute.children in place (see the caveat on buildAppRouter), and this one
// runs at module-eval time precisely so the typed tree is seeded once up front.
const TYPED_FULL_ROUTER = createRouter({
  routeTree: rootRoute.addChildren(ALL_ROUTES),
  // Typing-only: deny-by-default context so the root's RouterContext is
  // satisfied. Runtime mounts pass the real auth via buildAppRouter.
  context: { auth: undefined },
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof TYPED_FULL_ROUTER;
  }
}

export function buildAppRouter(section: Section, auth: CurrentUser | undefined) {
  // NB: rootRoute.addChildren() mutates rootRoute.children in place. This is
  // safe today because createRouter() snapshots the tree into routesByPath
  // synchronously and never re-reads rootRoute.children afterward, and
  // because no two AppRouter mounts are ever alive concurrently in
  // production — Turbo body-swaps remount on every page transition. The
  // proper fix is to refactor every route definition into a factory that
  // accepts a fresh rootRoute per call, but each route's component
  // currently self-references via `<route>.useParams()`, so converting them
  // means switching to `useParams({ from: '...' })` for all 20 routes.
  // Deferred to a separate ticket; covered by the AppRouter.test.tsx
  // invariants which assert each section sees only its own routes.
  const routeTree = rootRoute.addChildren(SECTION_ROUTES[section]);
  return createRouter({
    routeTree,
    defaultPreload: 'intent',
    defaultNotFoundComponent: CrossSectionFallback,
    // Threaded into router context so beforeLoad guards (requireCapability)
    // can read the signed-in user. Fail-closed: undefined → no capabilities.
    context: { auth },
  });
}

export default function AppRouter(props: AppRouterProps) {
  // Per-mount router so prefetch/match state doesn't survive Turbo navigations
  // (the mount remounts on each cross-page Turbo swap). The route tree is
  // scoped to props.section so popstate to a sibling section's URL hits
  // CrossSectionFallback instead of rendering against stale InitialContext.
  const router = useMemo(
    () => buildAppRouter(props.section, props.currentUser),
    [props.section, props.currentUser],
  );

  return (
    <InitialContext.Provider value={props}>
      <RouterProvider router={router} />
    </InitialContext.Provider>
  );
}
